export type BinanceCsvFormatKind =
  | "spot_trade_history"
  | "transaction_history"
  | "unknown";

function normalizeHeaderToken(raw: string): string {
  return raw
    .replace(/^\uFEFF/, "")
    .trim()
    .replace(/^["']|["']$/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

export function parseBinanceCsvHeaderLine(line: string): string[] {
  const trimmed = line.replace(/^\uFEFF/, "").trim();
  if (!trimmed) return [];
  return trimmed.split(",").map((cell) => cell.trim().replace(/^["']|["']$/g, ""));
}

export function detectBinanceCsvFormat(headerLine: string): BinanceCsvFormatKind {
  const tokens = parseBinanceCsvHeaderLine(headerLine).map(normalizeHeaderToken);
  if (!tokens.length) return "unknown";

  const has = (...names: string[]) => names.every((name) => tokens.includes(normalizeHeaderToken(name)));

  const isTransactionHistory =
    has("userid", "operation", "coin", "change") ||
    (has("operation", "coin", "change") && (has("time") || has("utctime")));

  const isSpotTradeHistory =
    (has("dateutc") || has("date")) &&
    (has("pair") || has("market")) &&
    (has("side") || has("type")) &&
    has("price") &&
    (has("executed") || has("amount"));

  if (isSpotTradeHistory) return "spot_trade_history";
  if (isTransactionHistory) return "transaction_history";
  return "unknown";
}

export function validateBinanceCsvForImport(fileText: string): {
  ok: boolean;
  kind: BinanceCsvFormatKind;
  title?: string;
  message?: string;
} {
  const firstLine = fileText.split(/\r?\n/).find((line) => line.trim()) ?? "";
  const kind = detectBinanceCsvFormat(firstLine);

  if (kind === "spot_trade_history") {
    return { ok: true, kind };
  }

  if (kind === "transaction_history") {
    return {
      ok: false,
      kind,
      title: "Wrong Binance export",
      message:
        "This file is Binance Transaction History (columns like User ID, Operation, Coin, Change). " +
        "Tessera needs Spot Trade History instead: on Binance go to Orders → Spot Order → Trade History, " +
        "set your date range, click Export, and upload that CSV (columns like Date(UTC), Pair, Side, Price, Executed, Amount, Fee). " +
        "Export in 3-month chunks if you need several years of history.",
    };
  }

  return {
    ok: false,
    kind,
    title: "Unrecognized Binance CSV",
    message:
      "This CSV does not look like Binance Spot Trade History. Export from Orders → Spot Order → Trade History " +
      "(not Wallet → Transaction History). The file should include Date(UTC), Pair, Side, Price, Executed, Amount, and Fee.",
  };
}

export function binanceCsvImportZeroTradesHint(kind: BinanceCsvFormatKind): string {
  if (kind === "transaction_history") {
    return (
      "No trades were imported. You likely uploaded Transaction History (wallet ledger). " +
      "Re-export Spot Trade History from Orders → Spot Order → Trade History and import that file instead."
    );
  }
  if (kind === "unknown") {
    return (
      "No trades were imported. Use Spot Trade History export (Date(UTC), Pair, Side, …), not Transaction History."
    );
  }
  return (
    "No trades were imported. Check the date range on the export, confirm the file has filled trades, " +
    "and try a fresh Spot Trade History CSV (3-month maximum per Binance export)."
  );
}

export function binanceCsvImportStatsAreEmpty(payload: unknown): boolean {
  const stats =
    payload && typeof payload === "object"
      ? ((payload as Record<string, unknown>).sync_stats ??
        (payload as Record<string, unknown>).syncStats)
      : null;
  if (!stats || typeof stats !== "object") return true;
  const row = stats as Record<string, unknown>;
  const nums = ["created", "Created", "updated", "Updated", "total", "Total"].map((key) =>
    Number(row[key])
  );
  return !nums.some((n) => Number.isFinite(n) && n > 0);
}
